(ns manage.feed-lookup-table.view
  (:require ["@mui/material/Chip$default" :as Chip]
            ["@mui/material/Tooltip$default" :as Tooltip]
            [common.widgets.checkbox :refer [checkbox]]
            [common.widgets.dropdown :refer [dropdown]]
            [common.widgets.table :as t]
            [manage.feed-lookup-table.form :as form]
            [mate.core :as mc]
            [reagent.core :as r]
            [uix.core :refer [defui $]]
            [uix.re-frame :refer [use-subscribe]]))

(def fields #{:latlon :postcode :multi})

(defn field-disabled?
  "The fields above are all exclusive, so if any of those
   is enabled, all other controls should be disabled
   for the current row"
  [settings fieldname current]
  (some #(boolean (get-in settings [fieldname %])) (disj fields current)))

(defui format-multi
  [{:keys [fieldname]}]
  (let [settings (use-subscribe [:form.feed-lookup/value :settings])
        disabled? (field-disabled? settings fieldname :multi)]
    ($ checkbox
       (merge (form/use-bind-control [:settings fieldname :multi])
              {:id (str "multi-" fieldname)
               :disabled? disabled?}))))

(defui format-separator
  [{:keys [fieldname]}]
  (let [settings (use-subscribe [:form.feed-lookup/value :settings])
        multi? (get-in settings [fieldname :multi])
        control (form/use-bind-control [:settings fieldname :separator])]
    (when multi?
      (r/as-element
       [dropdown
        (merge control
               {:options form/separator-options
                :sx {:width "100%"}})]))))

(defui format-latlon
  [{:keys [fieldname]}]
  (let [settings (use-subscribe [:form.feed-lookup/value :settings])
        ;; if latlon is enabled for any other field, the current control will be disabled
        latlon-disabled? (use-subscribe [:table.feed-lookup/latlon-disabled? fieldname])
        field-disabled? (field-disabled? settings fieldname :latlon)]
    ($ checkbox
       (merge (form/use-bind-control [:settings fieldname :latlon])
              {:id (str "latlon-" fieldname)
               :disabled? (or latlon-disabled? field-disabled?)}))))

(defui format-postcode
  [{:keys [fieldname]}]
  (let [settings (use-subscribe [:form.feed-lookup/value :settings])
        ;; if postcode is enabled for any other field, the current control will be disabled
        postcode-disabled? (use-subscribe [:table.feed-lookup/postcode-disabled? fieldname])
        disabled? (field-disabled? settings fieldname :postcode)]
    ($ checkbox
       (merge (form/use-bind-control [:settings fieldname :postcode])
              {:id (str "postcode-" fieldname)
               :disabled? (or disabled? postcode-disabled?)}))))

(defui format-postcode-type
  [{:keys [fieldname]}]
  (let [settings (use-subscribe [:form.feed-lookup/value :settings])
        postcode? (get-in settings [fieldname :postcode])
        control (form/use-bind-control [:settings fieldname :postcode-type])]
    (when postcode?
      (r/as-element
       [dropdown
        (merge control
               {:sx {:width "100%"}
                :options form/postcode-options})]))))

(defn columns
  [{:keys [show-latlon? show-postcode?]}]
  (cond-> [{:id :name-type
            :sort 1
            :label "fieldname"
            :format :fieldname
            :sortable? false}
           {:id :multi
            :sort 5
            :label "multi value"
            :format #($ format-multi %)
            :sortable? false}
           {:id :separator
            :sort 6
            :label "separator"
            :format #($ format-separator %)
            :sortable? false}]
    show-postcode? (conj {:id :latlon
                          :sort 2
                          :label "lat / long"
                          :format #($ format-latlon %)
                          :beta-tag {:tooltip-message (str "Lat/Long is still being developed. "
                                                           "If you would like to use this feature please reach out to the Creative Optimizations support team.")}
                          :sortable? false})
    show-latlon? (conj {:id :postcode
                        :sort 3
                        :label "postcode"
                        :format #($ format-postcode %)
                        :sortable? false}
                       {:id :postcode-type
                        :sort 4
                        :label "postcode type"
                        :format #($ format-postcode-type %)
                        :sortable? false})))

(defui row
  [{:keys [columns] :as lookup}]
  (r/as-element
   [t/table-row {:data-testid :table-feed-lookup-data-row}
    (doall
     (for [{:keys [id format class]} columns]
       [t/table-cell
        {:key id
         :class class
         :data-testid (str "table-feed-lookup__column--" (name id))}
        (format lookup)]))]))

(defui head
  [{:keys [label class beta-tag]}]
  (r/as-element
   [t/table-head {:class class}
    label
    (when (some? beta-tag)
      ($ Tooltip {:title (:tooltip-message beta-tag)}
         ($ Chip {:label "beta"
                  :size "small"})))]))

(defui table
  [{:keys [value-sub] :as props}]
  (let [{:keys [state value]} (use-subscribe [value-sub])
        search (use-subscribe [:table.feed-lookup/search-term])
        columns (columns props)]
    (r/as-element
     [t/table {:data-testid :table-feed-lookup}
      [t/table-row {}
       (for [{:keys [id] :as column} columns]
         ($ head (assoc column :key id)))]
      (cond
        (= :failure state) [t/table-message "We could not load any feed data, please provide a valid feed in the previous step."]
        (= :pending state) [t/table-message "Loading fields"]
        (and (not-empty search)
             (empty? value)) [t/table-message "No fields found for this term"]
        (empty? value) [t/table-message "We could not load any feed data, please provide a valid feed in the previous step."]
        (= :success state)
        (for [[idx fieldname] (mc/seq-indexed value)]
          ($ row {:key fieldname :idx idx :fieldname fieldname :columns columns})))])))
